import React from 'react';
import { Map } from 'immutable';
import { createPolicyRule, fetchTaskCategories, updatePolicyRule, fetchPolicyRule, fetchSources } from '../../../apis';
import { Form } from '../../form/Form';
var SYSTEM_DEFAULTS = ['Allow All', 'Deny All'];
var POLICY_TYPES = ['Category Access', 'Console Access', 'API Access'];
var CONSOLE_LIST = ['Categories', 'Dashboard', 'Engine', 'Environment', 'Errors', 'Handlers', 'Logs', 'Routines', 'Runs', 'Staged', 'System Errors', 'Trees', 'Access Keys', 'ApiV2', 'Console Access', 'Groups', 'Policy Rules', 'Setup', 'Sources', 'Users'];

var dataSources = function dataSources(_ref) {
  var policyName = _ref.policyName,
      policyType = _ref.policyType;
  return {
    categories: {
      fn: fetchTaskCategories,
      params: [{
        include: 'details'
      }],
      transform: function transform(result) {
        return result.categories;
      }
    },
    sources: {
      fn: fetchSources,
      params: [{
        include: 'details'
      }],
      transform: function transform(result) {
        return result.sources;
      }
    },
    policyRule: {
      fn: fetchPolicyRule,
      params: policyName && [{
        policyName: policyName,
        policyType: policyType,
        include: 'details,categories,consolePolicyRules,sources'
      }],
      transform: function transform(result) {
        return result.policyRule;
      }
    }
  };
};

var handleSubmit = function handleSubmit(_ref2) {
  var policyName = _ref2.policyName;
  return function (values) {
    return (policyName ? updatePolicyRule : createPolicyRule)({
      policy: values.toJS(),
      policyName: policyName,
      policyType: values.get('type')
    }).then(function (_ref3) {
      var policyRule = _ref3.policyRule,
          error = _ref3.error;

      if (error) {
        throw error.statusCode === 400 && error.message || 'There was an error saving the policy rule';
      }

      return policyRule;
    });
  };
};

var fields = function fields(_ref4) {
  var policyName = _ref4.policyName,
      policyType = _ref4.policyType;
  return function (_ref5) {
    var policyRule = _ref5.policyRule;
    return (!policyName || policyRule) && [{
      name: 'type',
      label: 'Type',
      type: policyRule ? 'text' : 'select',
      required: true,
      enabled: policyRule ? false : true,
      initialValue: policyRule ? policyRule.get('type') : '',
      options: POLICY_TYPES.map(function (v) {
        return {
          label: v,
          value: v
        };
      }),
      helpText: 'Type of policy rule, affecting where it will be applied.'
    }, {
      name: 'name',
      label: 'Name',
      type: policyType === 'System Default' ? 'select' : 'text',
      required: true,
      options: SYSTEM_DEFAULTS.map(function (v) {
        return {
          label: v,
          value: v
        };
      }),
      initialValue: policyRule ? policyRule.get('name') : '',
      helpText: 'User friendly name for the policy rule.',
      onChange: function onChange(_ref6, _ref7) {
        var values = _ref6.values;
        var setValue = _ref7.setValue;

        if (values.get('type') === 'System Default') {
          if (values.get('name') === 'Allow All') {
            setValue('rule', 'true');
            setValue('message', 'Allowed by default.');
          } else {
            setValue('rule', 'false');
            setValue('message', 'Not allowed by default.');
          }
        }
      }
    }, {
      name: 'rule',
      label: 'Rule',
      type: 'text',
      required: true,
      initialValue: policyRule ? policyRule.get('rule') : '',
      helpText: "Ruby expression that is evaluated. If true access is granted. Example: @identity.get_property('spaceAdmin') == 'true'"
    }, {
      name: 'message',
      label: 'Message',
      type: 'text',
      required: true,
      initialValue: policyRule ? policyRule.get('message') : '',
      helpText: 'Returned to the user if the rule is called and does not pass.'
    }, {
      name: 'categories',
      label: 'Categories',
      type: 'select-multi',
      required: false,
      visible: function visible(_ref8) {
        var values = _ref8.values;
        return values.get('type') === 'Category Access';
      },
      options: function options(_ref9) {
        var categories = _ref9.categories;
        return categories ? categories.map(function (category) {
          return Map({
            label: category.get('name'),
            value: category.get('name')
          });
        }) : [];
      },
      initialValue: policyRule ? policyRule.get('categories').map(function (category) {
        return category.get('name');
      }) : [],
      serialize: function serialize(_ref10) {
        var values = _ref10.values;
        return values.get('categories').map(function (name) {
          return name;
        });
      }
    }, {
      name: 'consolePolicyRules',
      label: 'Consoles',
      type: 'select-multi',
      required: false,
      visible: function visible(_ref11) {
        var values = _ref11.values;
        return values.get('type') === 'Console Access';
      },
      options: CONSOLE_LIST.map(function (v) {
        return {
          label: v,
          value: v
        };
      }),
      initialValue: policyType === 'Console Access' ? policyRule.get('consolePolicyRules').map(function (console) {
        return console.get('name');
      }) : [],
      serialize: function serialize(_ref12) {
        var values = _ref12.values;
        return values.get('consolePolicyRules') && values.get('consolePolicyRules').map(function (name) {
          return name;
        });
      }
    }, {
      name: 'sources',
      label: 'Sources',
      type: 'select-multi',
      required: false,
      visible: function visible(_ref13) {
        var values = _ref13.values;
        return values.get('type') === 'API Access';
      },
      options: function options(_ref14) {
        var sources = _ref14.sources;
        return sources ? sources.map(function (source) {
          return Map({
            label: source.get('name'),
            value: source.get('name')
          });
        }) : [];
      },
      initialValue: policyRule ? policyRule.get('sources').map(function (source) {
        return source.get('name');
      }) : [],
      serialize: function serialize(_ref15) {
        var values = _ref15.values;
        return values.get('sources') && values.get('sources').map(function (name) {
          return name;
        });
      }
    }];
  };
};

export var PolicyRuleForm = function PolicyRuleForm(_ref16) {
  var addFields = _ref16.addFields,
      alterFields = _ref16.alterFields,
      fieldSet = _ref16.fieldSet,
      formKey = _ref16.formKey,
      components = _ref16.components,
      onSave = _ref16.onSave,
      onError = _ref16.onError,
      children = _ref16.children,
      policyName = _ref16.policyName,
      policyType = _ref16.policyType;
  return /*#__PURE__*/React.createElement(Form, {
    addFields: addFields,
    alterFields: alterFields,
    fieldSet: fieldSet,
    formKey: formKey,
    components: components,
    onSubmit: handleSubmit,
    onSave: onSave,
    onError: onError,
    dataSources: dataSources,
    fields: fields,
    formOptions: {
      policyName: policyName,
      policyType: policyType
    }
  }, children);
};